import BasketSvg from "@/assets/svg/BasketSvg";
import EditPostSvg from "@/assets/svg/EditPostSvg";
import theme from "@/theme";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styles } from "../Home/stylesHome";

import { FC, MouseEvent, useState } from "react";
import { useGetUserByUidQuery } from "@/store/api/userApi";
import {
  useLikePostCommentMutation,
  useUnlikePostCommentMutation,
} from "@/store/api/postCommentApi";
import { useAppSelector } from "@/hooks/useAppSelector";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import Popup from "@/components/UI/popup/Popup";
import Mention from "./Mention";
import { setRetryText } from "@/store/slice/commentPendingSlice";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import ReportCommentSvg from "@/assets/svg/ReportCommentSvg";
import DotsMenu from "@/assets/svg/DotsMenu";
import { LikeSvg, UnLikeSvg } from "@/assets/svg/LikeSvg";
import { formatLikesCount } from "@/helpers/formatCount";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { RequireAuth } from "@/components/RequireAuth";

interface CommentData {
  uid: string;
  content: string;
  parentComment?: {
    avatar: string;
    username: string;
    comment: string;
    createdAt: string;
  };
  avatar: string;
}

interface PostCommentProps {
  uid: string;
  username?: string;
  handle?: string;
  zaps?: number;
  changed?: boolean;
  time?: string;
  comment?: string;
  likePost?: number;
  anchorEl?: HTMLElement | null;
  authorId?: string;
  open: boolean;
  handleClick?: (event: MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  handleDelete: (uid: string) => void;
  handleReport: (uid: string) => void;
  setReplyUid: (uid: string) => void;
  setOpenReplyModal?: any;
  setEdit: ({ uid, comment }: { uid: string; comment: string | undefined }) => void;
  setOpenEditModal?: any;
  isLiked?: boolean;
  status?: "pending" | "error" | "published";
  commentData?: CommentData;
  isOpenReplies: boolean;
  handleToggleReplies: () => Promise<void>;
  totalReplies: any;
  isLoading: boolean;
  level: number;
}

const PostComment: FC<PostCommentProps> = ({
  setReplyUid,
  setEdit,
  uid,
  username,
  handle,
  changed,
  time,
  comment,
  likePost,
  anchorEl,
  open,
  handleClick,
  handleClose,
  handleDelete,
  handleReport,
  authorId,
  setOpenReplyModal,
  setOpenEditModal,
  isLiked,
  status = "published",
  commentData,
  isOpenReplies,
  handleToggleReplies,
  totalReplies,
  isLoading,
  level,
}) => {
  // console.log("level PC", level);
  const dispatch = useAppDispatch();
  const [localIsLiked, setLocalIsLiked] = useState(isLiked);
  const [localLikesCount, setLocalLikesCount] = useState(likePost || 0);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const { user: authUser } = useAppSelector((state) => state.auth);
  const { data: user } = useGetUserByUidQuery(authorId!, {
    skip: !authorId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [likePostComment] = useLikePostCommentMutation();
  const [unlikePostComment] = useUnlikePostCommentMutation();
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = async (commentUid: string) => {
    if (isLiking) return;

    const prevLiked = localIsLiked;
    const prevLikes = localLikesCount;

    if (localIsLiked) {
      setLocalIsLiked(false);
      setLocalLikesCount((c) => c - 1);
    } else {
      setLocalIsLiked(true);
      setLocalLikesCount((c) => c + 1);
    }

    setIsLiking(true);
    try {
      if (prevLiked) {
        await unlikePostComment(uid).unwrap();
      } else {
        await likePostComment({ commentUid, type: "LIKE" }).unwrap();
      }
    } catch (error) {
      console.error("Failed to react:", error);

      setLocalIsLiked(prevLiked);
      setLocalLikesCount(prevLikes);

      setToast({
        message: "Something went wrong. Try again.",
        success: false,
      });
    } finally {
      setIsLiking(false);
    }
  };

  let avatarSrc;

  if (status !== "published") {
    avatarSrc = authUser?.image?.url;
  } else if (authorId === authUser?.uid) {
    avatarSrc = authUser?.image?.url;
  } else {
    avatarSrc = user?.image?.url;
  }

  const isDeleted = comment === "[deleted]";

  const onEditClick = () => {
    if (typeof setEdit === "function") {
      setEdit({ uid, comment });
    }
    if (commentData) {
      setOpenEditModal({
        open: true,
        uid: commentData.uid,
        comment: commentData.content,
        parentComment: commentData.parentComment,
        avatar: commentData.avatar,
      });
    }
    handleClose();
  };

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}

      <Stack
        direction={"row"}
        alignItems="flex-start"
        justifyContent={"center"}
        gap={"0.75rem"}
        sx={{
          paddingRight: "0.75rem",
        }}
      >
        <Popup uid={user?.uid || undefined} type={"user"}>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              zIndex: 2,
            }}
            src={avatarSrc}
          />
        </Popup>
        <Box width={"100%"}>
          <Box
            bgcolor={theme.palette.custom.blueGray100}
            sx={{
              padding: "0.25rem 0.5rem",
              borderRadius: "0.75rem",
            }}
          >
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
              <Stack direction={"row"} alignItems={"center"} gap={"0.75rem"}>
                <Stack direction={"row"} gap={"0.25rem"}>
                  <Typography
                    color={theme.palette.custom.blueGray900}
                    lineHeight={"24px"}
                    fontWeight={600}
                    fontSize={"15px"}
                  >
                    {handle}
                  </Typography>
                </Stack>

                <Typography
                  color={theme.palette.custom.mainBlack}
                  variant="body1"
                  lineHeight={"1.125rem"}
                  fontWeight={500}
                >
                  {changed ? "edited" : ""}
                </Typography>
              </Stack>
              <Typography
                fontSize="12px"
                fontWeight={500}
                lineHeight="14px"
                letterSpacing="-1%"
                fontFamily="Inter"
                textAlign="right"
                color={
                  status === "error"
                    ? theme.palette.custom.secondaryRed
                    : theme.palette.custom.blueGray600
                }
              >
                {status === "error" ? "Not published" : time}
              </Typography>
            </Stack>
            <Typography
              fontSize={"0.9375rem"}
              fontWeight={500}
              width={"100%"}
              lineHeight={"1.5rem"}
              color={theme.palette.custom.blueGray900}
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {comment
                ?.split(/(@\S+)/g)
                .map((part, index) =>
                  part.startsWith("@") && part.length > 1 ? (
                    <Mention key={index} mention={part} />
                  ) : (
                    <span key={index}>{part}</span>
                  )
                )}
            </Typography>
          </Box>
        </Box>
        {!isDeleted && status === "published" && authorId ? (
          <Tooltip
            title={authorId === authUser?.uid ? "Edit or delete" : "Report"}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  background: "#000000CC",
                  borderRadius: "4px",
                  px: "8px",
                  py: "4px",
                },
              },
            }}
          >
            <IconButton
              sx={{
                height: "22px",
                width: "22px",
                margin: "auto 0",
                padding: "0",
              }}
              id="post-menu-button"
              aria-controls={open ? "post-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <DotsMenu />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ width: "24px", height: "24px" }} />
        )}
        <Menu
          id="post-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={() => {
            handleClose();
          }}
          MenuListProps={{
            "aria-labelledby": "post-menu-button",
          }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          PaperProps={styles.postSelectDropdownMenu}
        >
          {authorId === authUser?.uid
            ? [
                <MenuItem
                  key={"edit"}
                  onClick={onEditClick}
                  sx={{
                    gap: "0.25rem",
                    width: "154px",
                    height: "36px",
                    padding: "8px 16px",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <EditPostSvg height={16} width={16} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        verticalAlign: "middle",
                      }}
                    >
                      Edit comment
                    </Typography>
                  </ListItemText>
                </MenuItem>,

                <MenuItem
                  key={"delete"}
                  onClick={() => {
                    handleDelete(uid);
                    handleClose();
                  }}
                  sx={{
                    gap: "0.25rem",
                    width: "154px",
                    padding: "8px 8px 8px 16px",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <BasketSvg style={{ width: "1rem", height: "1rem" }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      color="#CE212B"
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        verticalAlign: "middle",
                      }}
                    >
                      Delete comment
                    </Typography>
                  </ListItemText>
                </MenuItem>,
              ]
            : [
                <MenuItem
                  onClick={() => {
                    handleReport(uid);
                    handleClose();
                  }}
                  sx={{
                    gap: "4px",
                    height: "32.1px",
                    borderRadius: "8px",
                    padding: "6px 13px",
                    opacity: 1,
                    transform: "rotate(0deg)",
                    display: "flex",
                    alignItems: "center",
                  }}
                  key={"report"}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <ReportCommentSvg style={{ width: "1rem", height: "1rem" }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography
                      sx={{
                        width: "111px",
                        color: "#CE212B",
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        verticalAlign: "middle",
                      }}
                    >
                      Report comment
                    </Typography>
                  </ListItemText>
                </MenuItem>,
              ]}
        </Menu>
      </Stack>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        sx={{
          marginLeft: "3.625rem",
          marginRight: "2.875rem",
        }}
      >
        <Stack direction={"row"} gap={"20px"}>
          {status === "error" ? (
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "15px",
                lineHeight: "24px",
                letterSpacing: "-1%",
                whiteSpace: "pre-wrap",
              }}
            >
              <Box component="span" sx={{ color: "#2A2D31" }}>
                Something went wrong. Please{" "}
              </Box>
              <Box
                component="span"
                sx={{
                  color: theme.palette.custom.mainBlue,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => dispatch(setRetryText({ uid: uid, text: comment || "" }))}
              >
                Try again
              </Box>
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                height: "30px",
                alignItems: "center",
                gap: "20px",
                marginTop: "4px",
              }}
            >
              <RequireAuth>
                <Button
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "16px",
                    color: localIsLiked
                      ? theme.palette.primary.main
                      : theme.palette.custom.blueGray900,
                    minWidth: "fit-content",
                    margin: 0,
                    padding: 0,
                    gap: "2px",
                  }}
                  onClick={() => status === "published" && handleLike(uid)}
                  disabled={status !== "published" || isLiking}
                >
                  {!isDeleted &&
                    (status === "pending" ? (
                      "Publishing..."
                    ) : localIsLiked ? (
                      <LikeSvg />
                    ) : (
                      <UnLikeSvg />
                    ))}
                  {!isDeleted && status === "published" && formatLikesCount(localLikesCount)}
                </Button>
              </RequireAuth>

              {status === "published" && !isDeleted && (
                <RequireAuth>
                  <Button
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: "500",
                      lineHeight: "16px",
                      color: theme.palette.custom.blueGray900,
                      minWidth: "fit-content",
                      margin: 0,
                      padding: 0,
                    }}
                    onClick={() => {
                      setReplyUid(uid);
                      setOpenReplyModal(() => ({
                        comment: comment,
                        open: true,
                        uid: uid,
                        handle,
                        username,
                        avatar: user?.image?.url,
                        time: time,
                        level: level + 1,
                      }));
                    }}
                  >
                    Reply
                  </Button>
                </RequireAuth>
              )}
              {!isOpenReplies && totalReplies > 0 && (
                <Button
                  sx={{
                    textTransform: "none",
                    color: isLoading
                      ? theme.palette.custom.mainBlueDisable
                      : theme.palette.custom.mainBlue,
                    fontSize: "14px",
                    lineHeight: "16px",
                    fontWeight: 500,
                    margin: 0,
                    padding: 0,
                    gap: "6px",
                  }}
                  onClick={handleToggleReplies}
                >
                  {`View all ${totalReplies} replies`}
                  {isLoading && <CustomLoader size={18} />}
                </Button>
              )}
              {isOpenReplies && totalReplies > 0 && (
                <Button
                  sx={{
                    textTransform: "none",
                    color: theme.palette.custom.mainBlue,
                    fontSize: "14px",
                    lineHeight: "16px",
                    fontWeight: 500,
                    margin: 0,
                    padding: 0,
                  }}
                  onClick={handleToggleReplies}
                >
                  {`Hide replies`}
                </Button>
              )}
            </Box>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default PostComment;
